export default {
  title: "Notifications",
  subtitle: "Continuously monitors your projects and flags what needs action before it gets expensive",
  empty: "No open notifications",
  empty_unread: "No unread notifications",
  empty_filtered: "No notifications for this selection",
  loading: "Loading notifications …",
  show_all: "Show all",
  mark_all_read: "Mark all as read",
  mark_read: "Mark as read",
  read: "Read",
  unread: "unread",
  evaluated_at: "Last checked: {time}",
  severity: {
    INFO: "Information",
    WARNING: "Warning",
    CRITICAL: "Critical",
  },
  severity_meaning: {
    INFO: "General information",
    WARNING: "Action needed",
    CRITICAL: "Immediate intervention needed",
  },
  filter: {
    severity: "Urgency",
    type: "Type",
    all: "All",
  },
  types: {
    UNBILLED_SERVICES: "Unbilled services",
    MISSING_CONSTRUCTION_REPORT: "Overdue [[cr.pl]]",
    PROJECT_LOSS_RISK: "Looming loss",
    PROJECT_BUDGET_AT_RISK: "Budget at risk",
    PROJECT_COST_DRIFT: "Margin declining (cost drift)",
    MONTAGE_BEHIND_PLAN: "Progress behind plan",
    TIME_ENTRY_MISSING: "Missing time entries",
    TIME_ENTRY_APPROVAL_PENDING: "Times awaiting approval",
    TIME_ENTRY_RUNNING_STALE: "Clock still running",
    DOCUMENT_DUE: "Documents expiring",
  },
  messages: {
    TIME_ENTRY_MISSING: "{employee} is missing {count} working days, the oldest for {days} days ({oldestDate}).",
    TIME_ENTRY_APPROVAL_PENDING:
      "{count} recorded days from {employees} employees are waiting for approval, the oldest for {days} days.",
    TIME_ENTRY_RUNNING_STALE:
      "{employee}'s clock has been running since {startedOn} ({days} days). No new day can be started " +
      "until it is stopped.",
    UNBILLED_SERVICES: "{count} [[hwr.pl]] have been unbilled for {days} days (oldest from {oldestDate}).",
    MISSING_CONSTRUCTION_REPORT: "{count} work days have no [[cr]]. Oldest open day: {oldestDate} ({days} days ago).",
    PROJECT_LOSS_RISK:
      "Projected margin is {marginPercent} %: {costToDate} {currency} of cost against {contractValue} {currency} contract value.",
    PROJECT_BUDGET_AT_RISK:
      "{usedPercent} % of the budget is used: {costToDate} {currency} of {budget} {currency}. No completion has been recorded.",
    PROJECT_BUDGET_AT_RISK_MONTAGE:
      "{usedPercent} % of the budget is used, but only {completionPercent} % of the parts list is mounted: {leadPoints} percentage points ahead ({costToDate} {currency} of {budget} {currency}).",
    PROJECT_BUDGET_AT_RISK_SERVICE_CATALOG:
      "{usedPercent} % of the budget is used, but the service catalogue is only {completionPercent} % complete: {leadPoints} percentage points ahead ({costToDate} {currency} of {budget} {currency}).",
    PROJECT_BUDGET_AT_RISK_MANUAL:
      "{usedPercent} % of the budget is used at {completionPercent} % completion: {leadPoints} percentage points ahead ({costToDate} {currency} of {budget} {currency}).",
    PROJECT_BUDGET_AT_RISK_LEGACY:
      "{usedPercent} % of the budget is used: {costToDate} {currency} of {budget} {currency}.",
    PROJECT_BUDGET_AT_RISK_OVER:
      "The budget is exceeded: {costToDate} {currency} of {budget} {currency} ({usedPercent} %).",
    PROJECT_COST_DRIFT:
      "The projected margin fell {marginDropPercent} percentage points over the last {windowDays} days, down to {marginPercent} %.",
    MONTAGE_BEHIND_PLAN:
      "Montage progress is at {actualPercent} % instead of the expected {expectedPercent} %: {pendingCount} of {elementCount} positions are still open (planned montage end: {plannedEndDate}).",
    MONTAGE_BEHIND_PLAN_SERVICE_CATALOG:
      "Progress is at {actualPercent} % instead of the expected {expectedPercent} %: {pendingCount} of {elementCount} positions are still open (planned project end: {plannedEndDate}).",
    DOCUMENT_DUE: '{count} document(s) on {ownerTitle} are expiring, most urgently "{fileTitle}" on {dueDate}.',
  },
  settings: {
    title: "Notifications",
    active_count: "{active} of {total} monitors active",
    groups: {
      billing: "Billing & reports",
      costs: "Costs & progress",
      operations: "Time & documents",
    },
    units: {
      days: "days",
      days_before: "days before",
      points: "pp",
    },
    columns: {
      warning: "Warning",
      critical: "Critical",
      scope: "Period",
    },
    lookback: "Look back",
    unbilled: {
      title: "Unbilled services",
      hint:
        "Reports a project once its oldest unbilled [[hwr]] is this many days old. " +
        "An invoice draft does not count as billed.",
    },
    report_gap: {
      title: "Overdue [[cr.pl]]",
      hint:
        "Reports work days within a project's montage period that still have no [[cr]] " +
        "after this many days. The oldest missing day decides.",
      lookback_hint: "How far back missing reports are searched for. Older gaps are no longer reported.",
    },
    loss: {
      title: "Looming losses",
      hint:
        "Reports projects whose projected margin gets too thin or whose budget use runs ahead of " +
        "completion. A negative margin is always critical.",
      margin: "Margin below",
      margin_hint: "Warn as soon as a project's projected margin falls below this value.",
      budget_lead: "Budget lead",
      budget_lead_hint:
        "How far budget use may run ahead of completion. Critical from twice that; without a completion, warn from 100 minus this value % and critical from 100 % used.",
    },
    drift: {
      title: "Cost drift",
      hint:
        "Early warning: a project's projected margin dropped by at least this many percentage points " +
        "within the window. Once a loss looms, the “Looming losses” alert takes over.",
    },
    montage: {
      title: "Service progress",
      hint: "Compares the service catalogue's completion with even progress across the project period. After the planned project end an open position is always critical.",
    },
    timetracking: {
      title: "Time tracking",
      missing: "Days without recorded time",
      approval: "Pending approval",
      hint:
        "Reports days without recorded time, entries waiting too long for approval, and a clock nobody " +
        "stopped. Public holidays are not modelled in the product, so a gap is a hint rather than a " +
        "certainty and the alert stays a warning.",
      lookback_hint:
        "Bounds how far back the scan reaches: without it the first run would report the whole history at once.",
    },
    document_due: {
      title: "Expiring documents",
      hint:
        "These count down to the due date, so the critical level is the one closer to it than the " +
        "warning. Individual files can carry their own notice period in the file dialog: a driving " +
        "licence is worth a month, a service appointment three days. An expired document is always " +
        "critical.",
    },
    email: {
      title: "E-mail notifications",
      hint: "When enabled, new warnings and critical alerts are e-mailed automatically to exactly the users who are allowed to see them in the app.",
    },
  },
}
